import {reportError} from './error';

interface ImageErrorRecord {
  timestamp: number;
  url: string;
  error: string;
}

class ImageErrorTracker {
  private errorRecords: ImageErrorRecord[] = [];
  private readonly maxErrors = 20;
  private readonly timeWindow = 60000; // 1分钟 = 60000毫秒

  /**
   * 记录图片加载失败
   * @param url 图片URL
   * @param error 错误信息
   */
  recordError(url: string, error: string): void {
    const now = Date.now();
    
    // 添加新的错误记录
    this.errorRecords.push({
      timestamp: now,
      url,
      error
    });

    // 清理超过1分钟的错误记录
    this.cleanupOldRecords(now);

    // 检查是否需要上报错误
    this.checkAndReport(now);
  }

  /**
   * 清理超过时间窗口的错误记录
   * @param currentTime 当前时间
   */
  private cleanupOldRecords(currentTime: number): void {
    this.errorRecords = this.errorRecords.filter(
      record => currentTime - record.timestamp < this.timeWindow
    );
  }

  /**
   * 检查是否需要上报错误
   * @param currentTime 当前时间
   */
  private checkAndReport(currentTime: number): void {
    if (this.errorRecords.length >= this.maxErrors) {
      // 创建汇总错误信息
      const errorSummary = this.createErrorSummary();
      
      // 上报错误
      const error = new Error(`图片加载失败次数过多: ${this.errorRecords.length}次`);
      error.name = 'ImageLoadErrorThreshold';
      
      reportError(error, errorSummary, 'image-load-error-tracker');
      
      // 清空记录，避免重复上报
      this.errorRecords = [];
    }
  }

  /**
   * 创建错误汇总信息
   */
  private createErrorSummary(): string {
    const errorDetails = this.errorRecords.map(record => 
      `URL: ${record.url}, 时间: ${new Date(record.timestamp).toISOString()}, 错误: ${record.error}`
    ).join('\n');

    return `在1分钟内检测到${this.errorRecords.length}次图片加载失败:\n${errorDetails}`;
  }

  /**
   * 获取当前错误记录数量
   */
  getErrorCount(): number {
    return this.errorRecords.length;
  }

  /**
   * 获取距离下次上报还需要多少错误
   */
  getErrorsUntilReport(): number {
    return Math.max(0, this.maxErrors - this.errorRecords.length);
  }
}

// 创建全局实例
export const imageErrorTracker = new ImageErrorTracker();

/**
 * 记录图片加载失败并检查是否需要上报
 * @param url 图片URL
 * @param error 错误信息
 */
export const recordImageError = (url: string, error: string): void => {
  imageErrorTracker.recordError(url, error);
};

/**
 * 获取当前错误统计信息
 */
export const getImageErrorStats = () => {
  return {
    errorCount: imageErrorTracker.getErrorCount(),
    errorsUntilReport: imageErrorTracker.getErrorsUntilReport(),
    maxErrors: 5,
    timeWindow: '1分钟'
  };
};
